/* eslint-disable no-useless-escape */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { RoleEnum, GetEntityQuery, CreateUserInput, GetUserQuery, APIService, GetRoleQuery } from 'app/API.service';
import { EntityService } from '../entity.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilsService } from '../utils.service';
import { NGXLogger } from 'ngx-logger';
import { FrameworkGroup, UserService } from '../user.service';
import { AppLevelRoleEnum, AppLevelRoleEnumBnB } from '../enums/appLevelRoles.enum';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { RootEntityContextService } from '../root-entity-context.service';

interface DropDown {
  id: string;
  name: string;
}

@Component({
  selector: 'cygov-add-participant-modal',
  templateUrl: './add-participant-modal.component.html',
  styleUrls: ['./add-participant-modal.component.scss'],
  standalone: false,
})
export class AddParticipantModalComponent implements OnInit {
  @Input() collectionMode = false;
  @Input() isInformatica = false;
  @Input() isFromBulkVendor = false;
  @Input() framework: FrameworkGroup;
  @Input() currentSubId: string;
  @Input() rolesList: GetRoleQuery[];
  @Input() subEntitiesList: GetEntityQuery[];
  @Output() modalResult = new EventEmitter<{
    newUser: CreateUserInput;
    frameworkId: string;
    assessmentId: string;
  }>();
  @Output() modalResultCollection = new EventEmitter<CreateUserInput>();
  entity: GetEntityQuery;
  filteredSubEntityList: DropDown[] = [];
  newUser: CreateUserInput;
  selectedEntity: string;
  currentUser: GetUserQuery;
  roleOptions: string[] = [];
  leaderTypes = ['SubEntityLeader'];
  subEntity: GetEntityQuery;
  subEntityList: GetEntityQuery[];
  leaderType: string;
  roleEnum = RoleEnum;
  notAllowedRoles: string[] = [AppLevelRoleEnum.PARTICIPANT];
  currentUserRole: GetRoleQuery; // Adding currentUserRole which will be removed and replaced with user.role after making connection.
  isBnb = UtilsService.isBnB;
  divID = '';
  regID = '';
  isVendor: boolean = false;
  isThirdPartyView: boolean = false;

  constructor(
    private entityService: EntityService,
    private api: APIService,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    public activeModal: NgbActiveModal,
    private logger: NGXLogger,
    private userService: UserService,
    private customApi: CUSTOMAPIService,
    private router: Router,
    private rootEntityContextService: RootEntityContextService
  ) {}

  async ngOnInit(): Promise<void> {
    this.isThirdPartyView = this.router.url.includes('third-party') && this.router.url.includes('collection');
    this.currentUser = await this.userService.getCurrentUser();
    this.currentUserRole = this.userService.getCurrentRole();
    this.isVendor = this.currentUserRole.name.toLowerCase() === RoleEnum.VENDOR.toLowerCase();
    this.newUser = this.initUser();
    this.populateRoleOptions();
    const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    this.entity = entityId ? await this.customApi.GetEntity(entityId) : null;
    try {
      if (this.entity) {
        if (this.entityService.isRootEntity(this.entity)) {
          this.subEntityList = await this.rootEntityContextService.getSubEntities('CHILD_ENTITY');
          this.leaderTypes = [...this.leaderTypes, 'EntityLeader'];
        } else {
          this.toastrService.error('Unauthorized');
        }
      }
    } catch (e) {
      this.logger.error('Add Participant Modal Component - Error: ', e);
      const message = UtilsService.msgFromError(e);
      this.toastrService.error(message);
    }
    if (this.isBnb && this.entity) {
      this.divID = await this.entityService.getDivisionRegionId(true, this.entity?.id);
      this.regID = await this.entityService.getDivisionRegionId(false, this.entity?.id);
    }
  }

  getDisplayEntityId(): string {
    return this.entityService.isChildEntity(this.entity) ? this.entity.parentId : this.entity.id;
  }

  isValid(user: CreateUserInput): boolean {
    let valid = true;
    const re =
      // eslint-disable-next-line max-len
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!re.test(String(user.email).toLowerCase())) {
      this.toastrService.error('Email is incorrect');
      valid = false;
    }
    if (!user.name) {
      this.toastrService.error('User name is required');
      valid = false;
    }
    if (!user.roleId && !this.notAllowedRoles.includes(this.currentUserRole.name.toLowerCase())) {
      this.toastrService.error('User role is required');
      valid = false;
    } else if (
      user.role === RoleEnum.LEADER &&
      !this.notAllowedRoles.includes(this.currentUserRole.name.toLowerCase())
    ) {
      this.toastrService.error('Leader Type is required');
      valid = false;
    }
    return valid;
  }

  emitUser(newUser: CreateUserInput): void {
    newUser.email = newUser.email.toLowerCase();
    const tmpCurrentRole = this.rolesList.find(role => role.id === newUser.roleId)?.name;
    let subEntityId = UtilsService.getRouteParam(this.route.root.snapshot, 'subEntityId');
    if (!UtilsService.isDefined(subEntityId) && UtilsService.isDefined(this.currentSubId)) {
      subEntityId = this.currentSubId;
    }
    switch (tmpCurrentRole) {
      case AppLevelRoleEnum.SUBENTITY_LEADER:
      case AppLevelRoleEnum.PARTICIPANT:
      case AppLevelRoleEnum.VENDOR: {
        if (subEntityId) {
          newUser.entityIds = [subEntityId];
        }
        break;
      }
      case AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN:
      case AppLevelRoleEnum.ENTITY_LEADER: {
        newUser.entityIds = [this.entity.id];
        break;
      }
      case AppLevelRoleEnumBnB.DIVISION_LEADER: {
        newUser.entityLayerIds = [this.divID];
        break;
      }
      case AppLevelRoleEnumBnB.REGION_LEADER: {
        newUser.entityLayerIds = [this.regID];
        break;
      }
      default:
        break;
    }

    if (this.isValid(newUser)) {
      if (this.collectionMode) {
        this.modalResultCollection.emit(newUser);
      } else {
        const selectedSubEntity = this.subEntitiesList.find(subEntity => subEntity.id === this.selectedEntity);
        this.modalResult.emit({
          newUser,
          frameworkId: this.framework.name,
          assessmentId: selectedSubEntity.activeAssessmentId,
        });
      }
    }
  }
  initUser(): any {
    const roleId = this.isInformatica
      ? this.rolesList.find(r => r.name === AppLevelRoleEnum.ENTITY_LEADER)?.id
      : this.isVendor || this.isThirdPartyView
        ? this.rolesList.find(r => r.name === AppLevelRoleEnum.VENDOR)?.id
        : this.currentUserRole.id;
    return {
      name: '',
      email: '',
      roleId,
      isCognitoUser: false,
      questionMap: JSON.stringify({}),
    };
  }

  populateRoleOptions(): void {
    switch (this.currentUserRole.name.toLowerCase()) {
      case AppLevelRoleEnum.ADMIN:
        break;
      case AppLevelRoleEnum.MSSP:
        this.rolesList = this.rolesList.filter(role => role.name.toLowerCase() !== AppLevelRoleEnum.ADMIN);
        break;

      case AppLevelRoleEnum.ENTITY_LEADER:
        this.rolesList = this.rolesList.filter(
          role =>
            role.name.toLowerCase() !== AppLevelRoleEnum.ADMIN && role.name.toLowerCase() !== AppLevelRoleEnum.MSSP
        );
        break;

      case AppLevelRoleEnum.SUBENTITY_LEADER: {
        this.rolesList = this.rolesList.filter(
          role =>
            role.name.toLowerCase() !== AppLevelRoleEnum.ADMIN &&
            role.name.toLowerCase() !== AppLevelRoleEnum.MSSP &&
            role.name.toLowerCase() !== AppLevelRoleEnum.ENTITY_LEADER
        );
        break;
      }

      default: {
        this.rolesList = this.rolesList.filter(
          role =>
            role.name.toLowerCase() !== AppLevelRoleEnum.ADMIN && role.name.toLowerCase() !== AppLevelRoleEnum.MSSP
        );
        // We will check if the current custom role has the root entity access or not.
        if (
          this.currentUserRole &&
          this.currentUserRole.screenPermissions &&
          this.currentUserRole.screenPermissions['first-party'] &&
          !this.currentUserRole.screenPermissions['first-party'].includes('upperdeck')
        ) {
          const ind = this.rolesList.findIndex(role => role.name === AppLevelRoleEnum.ENTITY_LEADER);
          this.rolesList.splice(ind, 1);
        }
      }
    }
  }
}
